import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowLeft, Download, Loader2, AlertCircle, Calendar, ChevronDown, FileText, FileSpreadsheet, RotateCcw, ShieldCheck, FlaskConical, Pencil, Save, X, Feather, Briefcase, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { Eye, Settings, TrendingUp, Target, BookOpen, LogOut } from "lucide-react";
import { HalfCircle } from "@/components/ui/HalfCircle";
import ReactMarkdown from "react-markdown";
import {
  ReportHeader,
  ExecutiveSummary,
  CriterionCard,
  TradeoffTable,
  KeyQuestions,
} from "@/components/report";
import { CitationModal } from "@/components/citations";
import { api, type VendorEvaluation, type GroundingSource, type LLMModelId, type CustomPrompts, type NarrativeRewriteContent } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import LLMSelectionModal from "@/components/modals/LLMSelectionModal";
import CustomPromptsModal from "@/components/modals/CustomPromptsModal";
import CustomPromptsDiffModal from "@/components/modals/CustomPromptsDiffModal";
import { GLEAN_DEMO_EVALUATION_ID, GLEAN_MOCK_EVALUATION } from "@/data/glean-mock";
import { downloadReportPdf, type PdfReportData } from "@/utils/downloadReportPdf";
import { downloadReportDocx, type WordReportData } from "@/utils/downloadReportDocx";

// Per-criterion colors
const CRITERION_ACCENT: Record<string, string> = {
  SEE: "#4DA6D4",
  CHANGE: "#9B4FD4",
  ADAPT: "#5BBFB5",
  USE: "#C47B9F",
  LEARN: "#5C6BC0",
  EXIT: "#C4785A",
};

// Map criterion IDs to icons (colored per criterion)
const CRITERION_ICONS: Record<string, React.ReactNode> = {
  SEE: <Eye className="w-6 h-6" style={{ color: CRITERION_ACCENT.SEE }} />,
  CHANGE: <Settings className="w-6 h-6" style={{ color: CRITERION_ACCENT.CHANGE }} />,
  ADAPT: <TrendingUp className="w-6 h-6" style={{ color: CRITERION_ACCENT.ADAPT }} />,
  USE: <Target className="w-6 h-6" style={{ color: CRITERION_ACCENT.USE }} />,
  LEARN: <BookOpen className="w-6 h-6" style={{ color: CRITERION_ACCENT.LEARN }} />,
  EXIT: <LogOut className="w-6 h-6" style={{ color: CRITERION_ACCENT.EXIT }} />,
};

const CRITERION_ICON_COMPONENTS: Record<string, React.ComponentType<{ className?: string }>> = {
  SEE: Eye, CHANGE: Settings, ADAPT: TrendingUp,
  USE: Target, LEARN: BookOpen, EXIT: LogOut,
};

const FRAMEWORK_META: Record<string, { question: string; description: string }> = {
  SEE: { question: "Can you see how the AI works?", description: "Evaluates whether you can see the prompts, models, and architecture being used." },
  CHANGE: { question: "Can you modify AI behavior?", description: "Assesses your ability to adjust prompts, models, and configurations." },
  ADAPT: { question: "Does the vendor keep pace with AI advancement?", description: "Measures how quickly the vendor adopts new AI capabilities." },
  USE: { question: "Is the tool actually useful?", description: "Determines if the tool provides real value versus the appearance of value." },
  LEARN: { question: "Do you build portable AI skills?", description: "Evaluates whether using the tool builds generalizable AI capabilities." },
  EXIT: { question: "Can you leave cleanly?", description: "Assesses the difficulty of transitioning away from the vendor." },
};

const SCORE_LABEL: Record<string, string> = {
  yes: "Met",
  partially: "Partially Met",
  no: "Not Met",
  insufficient: "Insufficient Info",
};

const SCORE_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  yes: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  partially: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  no: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200" },
  insufficient: { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200" },
};

const SCORE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  yes: CheckCircle2,
  partially: ({ className }: { className?: string }) => <HalfCircle className={className} />,
  no: XCircle,
  insufficient: HelpCircle,
};

// Mock data for demo mode (when no evaluationId is provided)
const MOCK_EVALUATION: VendorEvaluation = {
  id: "demo",
  vendor_id: "demo-vendor",
  vendor_name: "Nexus AI",
  vendor_category: "Enterprise Knowledge Management & AI Search",
  evaluation_date: new Date().toISOString(),
  executive_summary: {
    key_takeaway: "Nexus AI delivers strong enterprise search capabilities with genuine utility, but presents meaningful trade-offs in transparency, customizability, and vendor lock-in that warrant careful consideration.",
    sentiment: "neutral",
    paragraphs: [
      "Nexus AI solves a real problem effectively. Organizations struggling with fragmented knowledge systems and information silos will find immediate value in its cross-platform search and synthesis capabilities. The tool demonstrates genuine utility backed by measurable productivity improvements in customer case studies.",
      "The principal concerns center on organizational learning and long-term flexibility. Nexus operates as a black box—system prompts, model selection, and context handling are entirely opaque. Users cannot modify core AI behaviors or select alternative models. This limits both immediate optimization for specific use cases and the organization's ability to develop AI competency through tool usage.",
      "Additionally, proprietary data formats create significant switching costs. While source documents remain in their original systems, organizational enrichments and knowledge structures built within Nexus cannot be cleanly exported. Organizations should enter this relationship with clear awareness of these dependencies.",
    ],
  },
  criteria: [
    {
      criterion_id: "SEE",
      criterion_name: "SEE",
      subtitle: "Transparency & Visibility",
      score: "no",
      assessment: "Nexus provides no visibility into its AI operations. System prompts, model selection logic, and context handling approaches are not exposed to users. Documentation describes features but not mechanics. During evaluation, the vendor characterized these details as 'proprietary.' Users cannot observe how organizational data is processed, chunked, retrieved, or prioritized before being sent to models.",
      tradeoff: { gain: "Simplified interface without technical complexity", giveUp: "Ability to verify vendor optimization choices align with organizational interests; Zero organizational learning about AI mechanics" },
      questions: ["What visibility can enterprise customers obtain into system prompts and model selection?", "Is transparency on the product roadmap?", "How do you ensure AI optimization choices align with customer interests rather than cost minimization?"],
      why_it_matters: [],
      sources: [
        { uri: "https://nexusai.example.com/docs/architecture", title: "Architecture Overview", snippet: "Our AI pipeline uses proprietary optimization techniques to deliver the best results for enterprise search queries." },
        { uri: "https://nexusai.example.com/security", title: "Security & Privacy", snippet: "System prompts and model configurations are protected as trade secrets to maintain competitive advantage." },
      ],
    },
    {
      criterion_id: "CHANGE",
      criterion_name: "CHANGE",
      subtitle: "Customizability & Control",
      score: "partially",
      assessment: "Users can adjust certain retrieval parameters such as weighting recent versus historical documents and can create custom 'knowledge domains' that scope searches to specific data sources. However, the core AI layer—including prompts, model selection, and inference parameters—cannot be modified. Organizations are accepting the vendor's one-size-fits-all AI configuration decisions.",
      tradeoff: { gain: "Meaningful search behavior customization without technical overhead", giveUp: "Ability to optimize core AI layer for specific organizational needs or use cases" },
      questions: ["What additional customization capabilities are planned?", "Can enterprise customers access prompt modification in any tier?", "How do you handle edge cases where your default configuration doesn't serve a customer's needs?"],
      why_it_matters: [],
      sources: [
        { uri: "https://nexusai.example.com/docs/customization", title: "Customization Guide", snippet: "Create knowledge domains to scope searches to specific document collections. Adjust retrieval weights between recent and historical content." },
        { uri: "https://nexusai.example.com/enterprise", title: "Enterprise Features", snippet: "Enterprise tier includes advanced filtering options and custom taxonomies for organizational knowledge management." },
      ],
    },
    {
      criterion_id: "ADAPT",
      criterion_name: "ADAPT",
      subtitle: "Vendor Agility & Innovation",
      score: "partially",
      assessment: "Review of Nexus's release history shows two underlying model updates within the past 18 months, most recently incorporating GPT-4 class models for synthesis features. This demonstrates ongoing investment. However, the update cycle lags frontier model releases by approximately 3-6 months, and customers cannot independently adopt new models as they become available. Organizations wanting to leverage cutting-edge capabilities quickly will experience delays.",
      tradeoff: { gain: "Vendor-managed updates without migration burden", giveUp: "Ability to move at the pace of AI advancement; Dependence on vendor prioritization" },
      questions: [],
      why_it_matters: [],
      sources: [
        { uri: "https://nexusai.example.com/changelog", title: "Release Notes", snippet: "Version 3.2 (March 2024): Upgraded synthesis engine to GPT-4 class models for improved answer quality and coherence." },
      ],
    },
    {
      criterion_id: "USE",
      criterion_name: "USE",
      subtitle: "Genuine Utility & Value",
      score: "yes",
      assessment: "Nexus AI demonstrates clear utility. Customer case studies and evaluation testing confirm the tool meaningfully improves information retrieval across enterprise systems. Standard integrations with SharePoint, Confluence, Google Workspace, and Slack work reliably. Synthesis features produce useful, well-structured outputs. Organizations with genuine enterprise search pain points will find real productivity value. This is the vendor's core strength.",
      tradeoff: { gain: "Effective solution to a common, costly enterprise problem", giveUp: "" },
      questions: [],
      why_it_matters: [],
      sources: [
        { uri: "https://nexusai.example.com/case-studies/acme", title: "ACME Corp Case Study", snippet: "After implementing Nexus AI, ACME Corp reported 40% reduction in time spent searching for information across their 500-person engineering team." },
        { uri: "https://nexusai.example.com/integrations", title: "Integrations", snippet: "Native connectors for SharePoint, Confluence, Google Workspace, Slack, Notion, and 50+ enterprise applications." },
      ],
    },
    {
      criterion_id: "LEARN",
      criterion_name: "LEARN",
      subtitle: "Transferable Knowledge",
      score: "no",
      assessment: "Using Nexus AI teaches users how to use Nexus AI—and nothing more. The interface completely abstracts away AI concepts. Users develop no understanding of prompting, context management, model behavior, or the mechanics of how AI-powered search operates. This represents a significant missed opportunity. Organizations building AI fluency will need to invest in learning elsewhere; this tool contributes nothing to that capability development.",
      tradeoff: { gain: "Easy onboarding with minimal training requirements", giveUp: "Opportunity to build portable AI skills through daily tool usage; Workforce remains dependent on this specific vendor's interface" },
      questions: [],
      why_it_matters: [],
      sources: [
        { uri: "https://nexusai.example.com/docs/getting-started", title: "Getting Started", snippet: "Simply type your question in natural language. Our AI handles everything else automatically." },
      ],
    },
    {
      criterion_id: "EXIT",
      criterion_name: "EXIT",
      subtitle: "Portability & Migration",
      score: "no",
      assessment: "Organizational knowledge and custom configurations can only be exported in Nexus's proprietary format. While raw source documents remain in their original systems, any enrichment, tagging, relationship mapping, or synthesis created within Nexus is locked in. Migration would require rebuilding these knowledge structures from scratch. For organizations using Nexus extensively, switching costs would be substantial.",
      tradeoff: { gain: "", giveUp: "Clean migration path; All organizational investment in knowledge enrichment trapped with vendor" },
      questions: ["What is your data portability policy?", "Can enrichments and configurations be exported in standard formats?", "What does a typical customer migration away from Nexus look like?", "Are standard export formats on your development roadmap?"],
      why_it_matters: [],
      sources: [
        { uri: "https://nexusai.example.com/docs/export", title: "Data Export", snippet: "Export your knowledge base in Nexus Archive Format (.naf). This proprietary format preserves all relationships and metadata." },
        { uri: "https://nexusai.example.com/terms", title: "Terms of Service", snippet: "Upon contract termination, customers may request a data export within 30 days. Exports are provided in Nexus proprietary format." },
      ],
    },
  ],
  key_questions: [
    "How critical is enterprise search as an immediate pain point versus a problem you could solve incrementally with internal tools?",
    "Is your organization actively building AI capacity now, or is that a future priority? Do you have other investments that will build AI fluency?",
    "What is your tolerance for vendor lock-in given the rapid pace of AI advancement and uncertainty about future capabilities?",
    "Would this tool be a primary AI touchpoint for employees, or do you have a portfolio approach that includes tools with better capacity-building characteristics?",
    "How do you evaluate the trade-off between immediate productivity gains and long-term organizational capability development?",
    "What is your timeline for making AI competency decisions? Is there value in delaying adoption to preserve learning opportunities?",
  ],
  procurement_assessment: `## Competitive Analysis\nNexus AI competes in a crowded enterprise search market alongside Glean, Coveo, Elastic, and Microsoft Copilot. Its primary differentiator is cross-platform synthesis — aggregating results from multiple enterprise tools into a single unified answer. However, competitors like Glean offer similar capabilities with stronger transparency features. The vendor's reliance on proprietary formats limits flexibility in head-to-head comparisons.\n\n## Security & Compliance\nNexus AI holds SOC 2 Type II certification and claims GDPR compliance. The company provides a dedicated security whitepaper and maintains data residency options in the US and EU. However, details about encryption at rest, key management, and incident response SLAs are not publicly available on the website. A formal security questionnaire response should be requested during procurement.\n\n## Performance & Quality\nCustomer case studies indicate measurable productivity gains (30-40% reduction in search time). The platform demonstrates reliable uptime based on published status page history. Synthesis quality is generally strong for factual queries but may produce hallucinated connections in complex multi-source scenarios. Load testing results and latency benchmarks are not publicly disclosed.\n\n## Enterprise-Readiness\nNexus AI supports SSO (SAML 2.0, OIDC), role-based access control, and audit logging. Admin controls for data source management are mature. However, the platform lacks granular permission controls at the document level, which may be a concern for organizations with strict information barriers. The onboarding process is vendor-managed with limited self-service options.\n\n## Integration & Scalability\nThe platform integrates with 50+ enterprise applications including SharePoint, Confluence, Google Workspace, Slack, and Salesforce. API access is available on enterprise plans. However, the API is read-only with no webhook support for real-time event triggers. Scalability claims are supported by customer references up to 10,000 users, though published benchmarks beyond that threshold are unavailable.\n\n## Other Procurement Concerns\n- **Vendor financial stability**: Nexus AI has raised Series B funding ($45M) but has not disclosed revenue figures or path to profitability.\n- **Contract flexibility**: Standard contracts are annual with auto-renewal. Early termination carries a penalty.\n- **Data portability**: Exports are limited to proprietary format, creating significant switching costs.\n- **Support tiers**: Standard support is business-hours only; 24/7 support requires premium tier at additional cost.\n- **Reference customers**: The vendor can provide references in financial services and technology sectors but has limited presence in healthcare and government.`,
  status: "completed",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

const AI_PROVIDER_ERROR = "The AI provider is currently experiencing issues. Please try again later.";

/** Map raw procurement error strings to user-friendly messages */
function getFriendlyProcurementError(error: string): string {
  // Backend already sanitized the error — pass through
  if (error.includes("AI provider is currently experiencing issues")) {
    return error;
  }
  if (error.includes("429") || error.includes("RESOURCE_EXHAUSTED") || error.includes("RATE_LIMIT_EXCEEDED")) {
    return "Daily rate limit reached. Please try again tomorrow.";
  }
  if (error.includes("502") || error.includes("503")) {
    return AI_PROVIDER_ERROR;
  }
  if (error.includes("timeout") || error.includes("ETIMEDOUT")) {
    return AI_PROVIDER_ERROR;
  }
  if (error.includes("Failed to connect")) {
    return "Could not connect to the processing service. Please check that all services are running.";
  }
  return "An unexpected error occurred while generating the procurement assessment. Try re-running the evaluation.";
}

/** Map evaluation-level error_message to user-friendly text */
function getFriendlyEvaluationError(errorMessage: string): string {
  // Backend already sanitized the error — pass through
  if (errorMessage.includes("AI provider is currently experiencing issues")) {
    return errorMessage;
  }
  if (errorMessage.includes("RATE_LIMIT_EXCEEDED")) {
    return "Daily rate limit reached. Please try again tomorrow.";
  }
  if (errorMessage.includes("VENDOR_DATA_UNAVAILABLE")) {
    return "Could not retrieve enough vendor data to complete the evaluation. The vendor website may be inaccessible or the data may have expired. Try re-running the evaluation.";
  }
  return "The evaluation encountered an error and could not be completed. Try re-running the evaluation.";
}

/** Sanitize raw error JSON from criterion assessment text */
function sanitizeCriterionAssessment(assessment: string): string {
  if (
    assessment.includes("Gemini query failed") ||
    assessment.includes("Unable to retrieve vendor data") ||
    assessment.includes("AI provider is currently experiencing issues")
  ) {
    return "Insufficient information available for this criterion. The vendor data could not be retrieved during evaluation. Consider re-running the evaluation or requesting this information directly from the vendor.";
  }
  return assessment;
}

/**
 * Static Report Page - Professional PDF-style report view.
 * This page breaks out of the dashboard layout for a print-friendly view.
 */
export default function StaticReportPage() {
  const navigate = useNavigate();
  const { evaluationId } = useParams<{ evaluationId?: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [evaluation, setEvaluation] = useState<VendorEvaluation | null>(null);
  const [isLoading, setIsLoading] = useState(!!evaluationId);
  const [error, setError] = useState<string | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<"pdf" | "docx" | null>(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Citation modal state
  const [selectedCitations, setSelectedCitations] = useState<GroundingSource[]>([]);
  const [selectedCitationIndex, setSelectedCitationIndex] = useState(0);
  const [isCitationModalOpen, setIsCitationModalOpen] = useState(false);

  // Rerun evaluation state (admin only)
  const [showRerunModal, setShowRerunModal] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);

  // Custom prompts modal state (all users)
  const [showCustomPromptsModal, setShowCustomPromptsModal] = useState(false);
  const [showCustomPromptsDiffModal, setShowCustomPromptsDiffModal] = useState(false);
  const [vendorEvaluations, setVendorEvaluations] = useState<VendorEvaluation[]>([]);

  // Edit mode state
  const canEditReports = user?.can_edit_reports === true;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedEvaluation, setEditedEvaluation] = useState<VendorEvaluation | null>(null);

  // Voice mode state
  const [voiceMode, setVoiceMode] = useState<'analytical' | 'narrative' | 'consultant'>('analytical');
  const [narrativeData, setNarrativeData] = useState<NarrativeRewriteContent | null>(null);
  const [isRewritingNarrative, setIsRewritingNarrative] = useState(false);
  const [narrativeError, setNarrativeError] = useState<string | null>(null);
  const [consultantData, setConsultantData] = useState<NarrativeRewriteContent | null>(null);
  const [isRewritingConsultant, setIsRewritingConsultant] = useState(false);
  const [consultantError, setConsultantError] = useState<string | null>(null);

  // Refs
  const reportContentRef = useRef<HTMLDivElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  // Close download menu on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node)) {
        setShowDownloadMenu(false);
      }
    }
    if (showDownloadMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDownloadMenu]);

  // Scroll-to-bottom detection for Glean demo (auto-opens AI assistant)
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const hasTriggeredAssistantRef = useRef(false);
  const isGleanDemo = evaluationId === GLEAN_DEMO_EVALUATION_ID;

  useEffect(() => {
    if (!isGleanDemo || !showContent) return;
    const sentinel = bottomSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggeredAssistantRef.current) {
          hasTriggeredAssistantRef.current = true;
          window.dispatchEvent(new CustomEvent("open-ai-assistant"));
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isGleanDemo, showContent]);

  // Handle citation click
  const handleCitationClick = useCallback((sources: GroundingSource[], index: number) => {
    setSelectedCitations(sources);
    setSelectedCitationIndex(index);
    setIsCitationModalOpen(true);
  }, []);

  const handleCloseCitationModal = useCallback(() => {
    setIsCitationModalOpen(false);
    setSelectedCitations([]);
  }, []);

  const handleToggleVoice = useCallback(async () => {
    if (voiceMode === 'narrative') {
      setVoiceMode('analytical');
      return;
    }

    if (narrativeData) {
      setVoiceMode('narrative');
      return;
    }

    if (!evaluation || evaluation.id === 'demo') return;

    setIsRewritingNarrative(true);
    setNarrativeError(null);
    try {
      const result = await api.getNarrativeRewrite(evaluation.id);
      if (result.success && result.rewrite) {
        setNarrativeData(result.rewrite);
        setVoiceMode('narrative');
      } else {
        setNarrativeError(result.error || 'Failed to generate narrative rewrite');
        setTimeout(() => setNarrativeError(null), 5000);
      }
    } catch (err) {
      console.error('Narrative rewrite failed:', err);
      setNarrativeError(err instanceof Error ? err.message : 'Failed to generate narrative rewrite');
      setTimeout(() => setNarrativeError(null), 5000);
    } finally {
      setIsRewritingNarrative(false);
    }
  }, [voiceMode, narrativeData, evaluation]);

  const handleToggleConsultantVoice = useCallback(async () => {
    if (voiceMode === 'consultant') {
      setVoiceMode('analytical');
      return;
    }

    if (consultantData) {
      setVoiceMode('consultant');
      return;
    }

    if (!evaluation || evaluation.id === 'demo') return;

    setIsRewritingConsultant(true);
    setConsultantError(null);
    try {
      const result = await api.getConsultantRewrite(evaluation.id);
      if (result.success && result.rewrite) {
        setConsultantData(result.rewrite);
        setVoiceMode('consultant');
      } else {
        setConsultantError(result.error || 'Failed to generate consultant rewrite');
        setTimeout(() => setConsultantError(null), 5000);
      }
    } catch (err) {
      console.error('Consultant rewrite failed:', err);
      setConsultantError(err instanceof Error ? err.message : 'Failed to generate consultant rewrite');
      setTimeout(() => setConsultantError(null), 5000);
    } finally {
      setIsRewritingConsultant(false);
    }
  }, [voiceMode, consultantData, evaluation]);

  // Fetch evaluation data if evaluationId is provided
  useEffect(() => {
    if (!evaluationId) {
      setEvaluation(MOCK_EVALUATION);
      setIsLoading(false);
      return;
    }

    // Use hardcoded Glean mock data for the demo
    if (evaluationId === GLEAN_DEMO_EVALUATION_ID) {
      setEvaluation(GLEAN_MOCK_EVALUATION);
      setIsLoading(false);
      return;
    }

    async function fetchEvaluation() {
      try {
        setIsLoading(true);
        const { evaluation } = await api.getEvaluation(evaluationId!);
        setEvaluation(evaluation);
      } catch (err) {
        console.error("Failed to fetch evaluation:", err);
        setError(err instanceof Error ? err.message : "Failed to load evaluation");
      } finally {
        setIsLoading(false);
      }
    }

    fetchEvaluation();
  }, [evaluationId]);

  // Show content after a brief delay for smooth fade-in
  useEffect(() => {
    if (!isLoading && evaluation) {
      const contentTimer = setTimeout(() => {
        setShowContent(true);
      }, 100);
      return () => clearTimeout(contentTimer);
    }
  }, [isLoading, evaluation]);

  // The evaluation to display: edited version when editing, otherwise original
  const displayEvaluation = isEditing && editedEvaluation ? editedEvaluation : evaluation;

  // Transform evaluation to report format, merging voice rewrite when active
  const isNarrativeActive = voiceMode === 'narrative' && narrativeData !== null;
  const isConsultantActive = voiceMode === 'consultant' && consultantData !== null;

  const reportData = useMemo(() => {
    const evalSource = displayEvaluation;
    if (!evalSource) return null;

    const evalDate = new Date(evalSource.evaluation_date);
    const evaluationDate = evalDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });

    const narr = isNarrativeActive ? narrativeData : isConsultantActive ? consultantData : null;

    return {
      vendor: evalSource.vendor_name,
      category: evalSource.vendor_category,
      evaluationDate,
      version: "1.0",
      executiveSummary: {
        keyTakeaway: narr?.executive_summary.key_takeaway ?? evalSource.executive_summary.key_takeaway,
        sentiment: evalSource.executive_summary.sentiment,
        paragraphs: narr?.executive_summary.paragraphs ?? evalSource.executive_summary.paragraphs,
      },
      criteria: evalSource.criteria.map((c) => {
        const narrCriterion = narr?.criteria.find((nc) => nc.criterion_id === c.criterion_id);
        return {
          criterionId: c.criterion_id,
          title: c.criterion_name,
          subtitle: c.subtitle,
          score: c.score as "yes" | "partially" | "no",
          assessment: sanitizeCriterionAssessment(narrCriterion?.assessment ?? c.assessment),
          tradeoff: narrCriterion?.tradeoff ?? c.tradeoff,
          questions: c.questions,
          icon: CRITERION_ICONS[c.criterion_id] || <Eye className="w-6 h-6 text-slate-700" />,
          sources: c.sources,
        };
      }),
      keyQuestions: narr?.key_questions ?? evalSource.key_questions,
      procurementAssessment: narr?.procurement_assessment ?? evalSource.procurement_assessment,
      procurementError: evalSource.procurement_error,
    };
  }, [displayEvaluation, isNarrativeActive, narrativeData, isConsultantActive, consultantData]);

  const tradeoffData = useMemo(() => {
    if (!reportData) return [];
    return reportData.criteria.map((c) => ({
      criterion: c.title,
      score: c.score,
      gain: c.tradeoff.gain || "",
      giveUp: c.tradeoff.giveUp || "",
    }));
  }, [reportData]);

  const getWordReportData = useCallback((): WordReportData | null => {
    if (!reportData) return null;
    return {
      vendor: reportData.vendor,
      category: reportData.category,
      evaluationDate: reportData.evaluationDate,
      version: reportData.version,
      executiveSummary: reportData.executiveSummary,
      criteria: reportData.criteria.map((c) => ({
        title: c.title,
        subtitle: c.subtitle,
        score: c.score,
        assessment: c.assessment,
        tradeoff: c.tradeoff,
        questions: c.questions,
      })),
      keyQuestions: reportData.keyQuestions,
    };
  }, [reportData]);

  const getPdfReportData = useCallback((): PdfReportData | null => {
    if (!reportData) return null;
    return {
      vendor: reportData.vendor,
      category: reportData.category,
      evaluationDate: reportData.evaluationDate,
      version: reportData.version,
      executiveSummary: reportData.executiveSummary,
      criteria: reportData.criteria.map((c) => ({
        criterionId: c.criterionId,
        title: c.title,
        subtitle: c.subtitle,
        score: c.score,
        assessment: c.assessment,
        tradeoff: c.tradeoff,
        questions: c.questions,
        sources: c.sources,
      })),
      tradeoffs: reportData.criteria.map((c) => ({
        criterion: c.title,
        score: c.score,
        gain: c.tradeoff.gain || "",
        giveUp: c.tradeoff.giveUp || "",
      })),
      keyQuestions: reportData.keyQuestions,
      procurementAssessment: reportData.procurementAssessment,
    };
  }, [reportData]);

  const handleDownloadPdf = useCallback(async () => {
    const data = getPdfReportData();
    if (!data) return;
    setIsDownloading(true);
    setDownloadFormat("pdf");
    setShowDownloadMenu(false);
    try {
      const vendorSlug = data.vendor.toLowerCase().replace(/\s+/g, "-");
      await downloadReportPdf(data, `${vendorSlug}-evaluation-report.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
    } finally {
      setIsDownloading(false);
      setDownloadFormat(null);
    }
  }, [getPdfReportData]);

  const handleDownloadWord = useCallback(async () => {
    const data = getWordReportData();
    if (!data) return;
    setIsDownloading(true);
    setDownloadFormat("docx");
    setShowDownloadMenu(false);
    try {
      await downloadReportDocx(data);
    } catch (err) {
      console.error("Failed to generate Word document:", err);
    } finally {
      setIsDownloading(false);
      setDownloadFormat(null);
    }
  }, [getWordReportData]);

  const handleExit = () => {
    navigate("/analysis");
  };

  const handleRerunEvaluation = async (model: LLMModelId) => {
    if (!evaluation) return;
    setIsRerunning(true);
    setShowRerunModal(false);
    try {
      await api.startEvaluation(evaluation.vendor_id, { model });
      navigate("/analysis");
    } catch (err) {
      console.error("Failed to rerun evaluation:", err);
      setIsRerunning(false);
    }
  };

  const handleCustomPromptsRerun = async (model: LLMModelId, customPrompts?: CustomPrompts) => {
    if (!evaluation) return;
    setIsRerunning(true);
    setShowCustomPromptsModal(false);
    try {
      await api.startEvaluation(evaluation.vendor_id, { model, custom_prompts: customPrompts });
      navigate("/analysis");
    } catch (err) {
      console.error("Failed to rerun with custom prompts:", err);
      setIsRerunning(false);
    }
  };

  // Fetch vendor evaluations for "Load Previous" in custom prompts modal
  useEffect(() => {
    if (showCustomPromptsModal && evaluation?.vendor_id) {
      api.getVendorEvaluations(evaluation.vendor_id)
        .then((res) => setVendorEvaluations(res.evaluations))
        .catch(() => {});
    }
  }, [showCustomPromptsModal, evaluation?.vendor_id]);

  // ==================== Edit Mode Handlers ====================

  const handleStartEditing = () => {
    if (evaluation) {
      setVoiceMode('analytical');
      setEditedEvaluation(JSON.parse(JSON.stringify(evaluation)));
      setIsEditing(true);
    }
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedEvaluation(null);
  };

  const handleSaveEdits = async () => {
    if (!editedEvaluation || !evaluation) return;

    setIsSaving(true);
    try {
      const updates: Record<string, unknown> = {};

      // Check executive summary changes
      const origSummary = evaluation.executive_summary;
      const editSummary = editedEvaluation.executive_summary;
      if (
        origSummary.key_takeaway !== editSummary.key_takeaway ||
        origSummary.sentiment !== editSummary.sentiment ||
        JSON.stringify(origSummary.paragraphs) !== JSON.stringify(editSummary.paragraphs)
      ) {
        updates.executive_summary = editSummary;
      }

      // Check criteria changes
      const criteriaUpdates: { criterion_id: string; score?: string; assessment?: string; tradeoff?: { gain?: string; giveUp?: string } }[] = [];
      for (let i = 0; i < editedEvaluation.criteria.length; i++) {
        const orig = evaluation.criteria[i];
        const edit = editedEvaluation.criteria[i];
        if (!orig || !edit) continue;
        const changes: Record<string, unknown> = { criterion_id: edit.criterion_id };
        let hasChange = false;
        if (orig.score !== edit.score) { changes.score = edit.score; hasChange = true; }
        if (orig.assessment !== edit.assessment) { changes.assessment = edit.assessment; hasChange = true; }
        if (JSON.stringify(orig.tradeoff) !== JSON.stringify(edit.tradeoff)) { changes.tradeoff = edit.tradeoff; hasChange = true; }
        if (hasChange) criteriaUpdates.push(changes as typeof criteriaUpdates[number]);
      }
      if (criteriaUpdates.length > 0) updates.criteria = criteriaUpdates;

      // Check key questions changes
      if (JSON.stringify(evaluation.key_questions) !== JSON.stringify(editedEvaluation.key_questions)) {
        updates.key_questions = editedEvaluation.key_questions;
      }

      // Check procurement assessment changes
      if (evaluation.procurement_assessment !== editedEvaluation.procurement_assessment) {
        updates.procurement_assessment = editedEvaluation.procurement_assessment;
      }

      if (Object.keys(updates).length > 0) {
        const result = await api.updateEvaluation(evaluation.id, updates as any);
        setEvaluation(result.evaluation);
        setNarrativeData(null);
        setConsultantData(null);
      }

      setIsEditing(false);
      setEditedEvaluation(null);
    } catch (err) {
      console.error("Failed to save edits:", err);
      setError(err instanceof Error ? err.message : "Failed to save edits");
    } finally {
      setIsSaving(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f8f5f0] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#d4975c] animate-spin mx-auto mb-4" />
          <p className="text-[#5a6a7a]">Loading report...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#f8f5f0] flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-sans font-semibold text-[#1e3a5f] mb-2">Failed to Load Report</h2>
          <p className="text-[#5a6a7a] mb-6">{error}</p>
          <button
            onClick={handleExit}
            className="px-6 py-2 bg-[#d4975c] text-white rounded-lg hover:bg-[#c48a50] transition-colors"
          >
            Return to Reports
          </button>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <div ref={reportContentRef} className="min-h-screen bg-[#f8f5f0]">
      {/* Branded header - hidden in exports */}
      <motion.div
        data-export-hide
        initial={{ opacity: 0 }}
        animate={{ opacity: showContent ? 1 : 0 }}
        transition={{ duration: 0.6 }}
      >
        <ReportHeader
          vendorName={reportData.vendor}
          category={reportData.category}
          evaluationDate={reportData.evaluationDate}
          reportVersion={reportData.version}
        />
      </motion.div>

      {/* Clean export header - no branding, only visible in exports */}
      <div data-export-show>
        <div className="bg-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-2">
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-sans font-light tracking-tight text-white">
                  {reportData.vendor}
                </h1>
                <p className="text-slate-400 uppercase tracking-[0.25em] text-xs sm:text-sm font-medium mt-1">
                  Vendor Evaluation Report
                </p>
              </div>
              <div className="text-left sm:text-right text-sm">
                <div className="text-slate-300 mb-1">Version {reportData.version}</div>
                <div className="flex items-center gap-2 sm:justify-end text-slate-400">
                  <Calendar className="w-4 h-4" />
                  {reportData.evaluationDate}
                </div>
              </div>
            </div>
            <p className="text-slate-300 text-sm sm:text-base">{reportData.category}</p>
          </div>
        </div>
      </div>

      {/* Top Navigation - hidden in export and print */}
      <motion.div
        data-export-hide
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 print:hidden flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : -10 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={handleExit}
            className="inline-flex items-center gap-2 text-[#5a6a7a] hover:text-[#1e3a5f] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Reports</span>
          </button>
          {evaluation?.custom_prompts_used && evaluation?.custom_prompts && (
            <button
              onClick={() => setShowCustomPromptsDiffModal(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition-colors cursor-pointer"
              title="View custom prompt modifications"
            >
              <FlaskConical className="w-3 h-3" />
              Custom Prompts
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Narrative Voice Toggle — hidden for next release */}
          {false && evaluation?.status === 'completed' && evaluation?.id !== 'demo' && !isEditing && (
            <button
              onClick={handleToggleVoice}
              disabled={isRewritingNarrative}
              className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium shadow-sm transition-all text-sm sm:text-base ${
                voiceMode === 'narrative'
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 ring-2 ring-indigo-300'
                  : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:border-slate-400'
              }`}
              title={voiceMode === 'narrative' ? 'Switch to analytical voice' : 'Switch to narrative voice'}
            >
              {isRewritingNarrative ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Feather className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {isRewritingNarrative ? 'Rewriting...' : 'Narrative Voice'}
              </span>
              <span className="sm:hidden">
                {isRewritingNarrative ? '...' : 'Voice'}
              </span>
            </button>
          )}
          {false && narrativeError && (
            <span className="text-xs text-red-500 max-w-[200px] truncate" title={narrativeError ?? undefined}>
              {narrativeError}
            </span>
          )}
          {/* Consultant Voice Toggle — hidden for next release */}
          {false && evaluation?.status === 'completed' && evaluation?.id !== 'demo' && !isEditing && (
            <button
              onClick={handleToggleConsultantVoice}
              disabled={isRewritingConsultant}
              className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium shadow-sm transition-all text-sm sm:text-base ${
                voiceMode === 'consultant'
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700 ring-2 ring-emerald-300'
                  : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 hover:border-slate-400'
              }`}
              title={voiceMode === 'consultant' ? 'Switch to analytical voice' : 'Switch to consultant voice'}
            >
              {isRewritingConsultant ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Briefcase className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {isRewritingConsultant ? 'Rewriting...' : 'Consultant Voice'}
              </span>
              <span className="sm:hidden">
                {isRewritingConsultant ? '...' : 'Consult'}
              </span>
            </button>
          )}
          {false && consultantError && (
            <span className="text-xs text-red-500 max-w-[200px] truncate" title={consultantError ?? undefined}>
              {consultantError}
            </span>
          )}
          {/* Edit Mode Controls */}
          {canEditReports && evaluation?.status === 'completed' && !isGleanDemo && (
            isEditing ? (
              <>
                <button
                  onClick={handleSaveEdits}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-3 sm:px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-md disabled:opacity-60 text-sm sm:text-base"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> : <Save className="w-4 h-4 sm:w-5 sm:h-5" />}
                  <span>{isSaving ? "Saving..." : "Save"}</span>
                </button>
                <button
                  onClick={handleCancelEditing}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 px-3 sm:px-5 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors font-medium shadow-md disabled:opacity-60 text-sm sm:text-base"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Cancel</span>
                </button>
              </>
            ) : (
              <button
                onClick={handleStartEditing}
                className="inline-flex items-center gap-2 px-3 sm:px-5 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium shadow-md text-sm sm:text-base"
              >
                <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Edit Report</span>
                <span className="sm:hidden">Edit</span>
              </button>
            )
          )}
          {evaluation && !isGleanDemo && !isEditing && (
            <button
              onClick={() => setShowCustomPromptsModal(true)}
              disabled={isRerunning}
              className="inline-flex items-center gap-2 px-3 sm:px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium shadow-md disabled:opacity-60 text-sm sm:text-base"
            >
              <FlaskConical className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Custom Prompts</span>
              <span className="sm:hidden">Custom</span>
            </button>
          )}
          {isAdmin && evaluation && !isGleanDemo && !isEditing && (
            <button
              onClick={() => setShowRerunModal(true)}
              disabled={isRerunning}
              className="inline-flex items-center gap-2 px-3 sm:px-5 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#2a4a6f] transition-colors font-medium shadow-md disabled:opacity-60 text-sm sm:text-base"
            >
              {isRerunning ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> : <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />}
              <span>{isRerunning ? "Starting..." : "Rerun"}<span className="hidden sm:inline"> Evaluation</span></span>
            </button>
          )}
          <div ref={downloadMenuRef} className="relative">
            <button
              onClick={() => setShowDownloadMenu((v) => !v)}
              disabled={isDownloading}
              className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 bg-[#d4975c] text-white rounded-lg hover:bg-[#c48a50] transition-colors font-medium shadow-md disabled:opacity-60 text-sm sm:text-base"
            >
              {isDownloading ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" /> : <Download className="w-4 h-4 sm:w-5 sm:h-5" />}
              <span>{isDownloading ? `Generating ${downloadFormat === "docx" ? "Word" : "PDF"}...` : "Download"}</span>
              {!isDownloading && <ChevronDown className="w-4 h-4" />}
            </button>
            {showDownloadMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-xl border border-slate-200 py-1 z-50">
                <button
                  onClick={handleDownloadPdf}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <FileText className="w-4 h-4 text-red-500" />
                  <span>Download as PDF</span>
                </button>
                <button
                  onClick={handleDownloadWord}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <FileSpreadsheet className="w-4 h-4 text-blue-500" />
                  <span>Download as Word</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Failed Evaluation Banner */}
      {evaluation?.status === "failed" && evaluation?.error_message && (
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : -10 }}
          transition={{ duration: 0.5, delay: 0.12 }}
        >
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-red-800 font-medium">Evaluation Failed</p>
              <p className="text-red-700 text-sm mt-1">
                {getFriendlyEvaluationError(evaluation.error_message)}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Animated Executive Summary */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 30 }}
        transition={{ duration: 0.6, delay: 0.15 }}
      >
        <ExecutiveSummary
          keyTakeaway={reportData.executiveSummary.keyTakeaway}
          sentiment={reportData.executiveSummary.sentiment}
          summary={reportData.executiveSummary.paragraphs}
          isEditing={isEditing}
          onUpdate={(updates) => {
            if (!editedEvaluation) return;
            setEditedEvaluation({
              ...editedEvaluation,
              executive_summary: {
                ...editedEvaluation.executive_summary,
                ...(updates.key_takeaway !== undefined && { key_takeaway: updates.key_takeaway }),
                ...(updates.sentiment !== undefined && { sentiment: updates.sentiment as any }),
                ...(updates.paragraphs !== undefined && { paragraphs: updates.paragraphs }),
              },
            });
          }}
        />
      </motion.div>

      {/* Framework Breakdown Cards */}
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 30 }}
        transition={{ duration: 0.6, delay: 0.25 }}
      >
        <div className="mb-6">
          <h2 className="text-2xl font-sans font-semibold text-[#1e3a5f] mb-2">Evaluation Overview</h2>
          <p className="text-[#5a6a7a] text-sm">Each criterion at a glance</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportData.criteria.map((c) => {
            const meta = FRAMEWORK_META[c.criterionId];
            const Icon = CRITERION_ICON_COMPONENTS[c.criterionId] || Eye;
            const accent = CRITERION_ACCENT[c.criterionId] || "#D4893E";
            const scoreSt = SCORE_STYLES[c.score] || SCORE_STYLES.insufficient;
            const label = SCORE_LABEL[c.score] || "Unknown";
            return (
              <div
                key={c.criterionId}
                className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col gap-2"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: accent }}
                  >
                    <Icon className="w-4.5 h-4.5 text-white" />
                  </div>
                  <p className="font-semibold text-[#063F6B] text-sm flex-1 min-w-0">{c.title} &ndash; {c.subtitle}</p>
                  {(() => {
                    const ScoreIcon = SCORE_ICONS[c.score] || SCORE_ICONS.insufficient;
                    return (
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full flex-shrink-0 ${scoreSt.bg}`}>
                        <ScoreIcon className={`w-3.5 h-3.5 ${scoreSt.text}`} />
                        <span className={`text-xs font-semibold whitespace-nowrap ${scoreSt.text}`}>{label}</span>
                      </div>
                    );
                  })()}
                </div>
                {meta && (
                  <>
                    <p className="text-sm font-medium text-[#063F6B]/80 italic">{meta.question}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{meta.description}</p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Animated Detailed Evaluation */}
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 30 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="mb-6">
          <h2 className="text-3xl font-sans font-semibold text-[#1e3a5f] mb-2">Detailed Evaluation</h2>
          <motion.div
            className="h-1 w-20 bg-[#d4975c] rounded-full"
            initial={{ width: 0 }}
            animate={{ width: showContent ? 80 : 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          />
          <p className="text-[#5a6a7a] mt-3">
            Assessment across six critical criteria for AI vendor evaluation
          </p>

        </div>

        <div className="space-y-6">
          {reportData.criteria.map((criterion, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 20 }}
              transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
            >
              <CriterionCard
                title={criterion.title}
                subtitle={criterion.subtitle}
                coreQuestion={FRAMEWORK_META[criterion.criterionId]?.question}
                score={criterion.score}
                assessment={criterion.assessment}
                tradeoff={criterion.tradeoff}
                questions={criterion.questions}
                icon={criterion.icon}
                sources={criterion.sources}
                isEditing={isEditing}
                onUpdate={(updates) => {
                  if (!editedEvaluation) return;
                  const updatedCriteria = [...editedEvaluation.criteria];
                  const idx = updatedCriteria.findIndex(c => c.criterion_id === criterion.criterionId);
                  if (idx !== -1) {
                    updatedCriteria[idx] = {
                      ...updatedCriteria[idx],
                      ...(updates.score !== undefined && { score: updates.score }),
                      ...(updates.assessment !== undefined && { assessment: updates.assessment }),
                      ...(updates.tradeoff !== undefined && { tradeoff: { ...updatedCriteria[idx].tradeoff, ...updates.tradeoff } }),
                    };
                    setEditedEvaluation({ ...editedEvaluation, criteria: updatedCriteria });
                  }
                }}
              />
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Animated Tradeoff Table */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 30 }}
        transition={{ duration: 0.6, delay: 0.8 }}
      >
        <TradeoffTable tradeoffs={tradeoffData} />
      </motion.div>

      {/* Procurement Assessment */}
      {reportData.procurementAssessment ? (
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 30 }}
          transition={{ duration: 0.6, delay: 0.85 }}
        >
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-8 h-8 text-[#1e3a5f]" />
              <h2 className="text-3xl font-sans font-semibold text-[#1e3a5f]">Procurement Assessment</h2>
            </div>
            <motion.div
              className="h-1 w-20 bg-[#d4975c] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: showContent ? 80 : 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            />
            <p className="text-[#5a6a7a] mt-3">
              Enterprise procurement fitness evaluation covering key criteria for large-firm best practices
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8">
            {isEditing ? (
              <textarea
                className="w-full min-h-[300px] text-[#3a4a5a] leading-relaxed bg-white border border-slate-300 rounded-lg p-4 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#d4975c]/50 resize-y"
                value={reportData.procurementAssessment || ""}
                onChange={(e) => {
                  if (!editedEvaluation) return;
                  setEditedEvaluation({ ...editedEvaluation, procurement_assessment: e.target.value });
                }}
                placeholder="Procurement assessment (Markdown)"
              />
            ) : (
              <div className="prose prose-slate max-w-none prose-headings:text-[#1e3a5f] prose-headings:font-sans prose-h2:text-xl prose-h2:mb-3 prose-p:text-[#3a4a5a] prose-p:leading-relaxed prose-li:text-[#3a4a5a] prose-strong:text-[#1e3a5f]">
              <ReactMarkdown
                components={{
                  h2: ({ children, ...props }) => (
                    <h2 {...props} className="!mt-8 !pt-6 border-t border-slate-200 first:!mt-0 first:!pt-0 first:border-0">
                      {children}
                    </h2>
                  ),
                  h3: ({ children, ...props }) => (
                    <h3 {...props} className="!mt-6 !mb-2 text-lg font-semibold text-[#1e3a5f]">
                      {children}
                    </h3>
                  ),
                  hr: () => <hr className="my-6 border-slate-200" />,
                }}
              >
                {reportData.procurementAssessment}
              </ReactMarkdown>
              </div>
            )}

          </div>
        </motion.div>
      ) : isAdmin && reportData.procurementError ? (
        <motion.div
          data-export-hide
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 30 }}
          transition={{ duration: 0.6, delay: 0.85 }}
        >
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="w-8 h-8 text-[#1e3a5f]" />
              <h2 className="text-3xl font-sans font-semibold text-[#1e3a5f]">Procurement Assessment</h2>
            </div>
            <motion.div
              className="h-1 w-20 bg-[#d4975c] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: showContent ? 80 : 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-amber-800 font-medium mb-1">Procurement assessment unavailable</p>
                <p className="text-amber-700 text-sm">
                  {getFriendlyProcurementError(reportData.procurementError)}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}

      {/* Animated Key Questions */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 30 }}
        transition={{ duration: 0.6, delay: 0.95 }}
      >
        <KeyQuestions
          questions={reportData.keyQuestions}
          isEditing={isEditing}
          onUpdate={(questions) => {
            if (!editedEvaluation) return;
            setEditedEvaluation({ ...editedEvaluation, key_questions: questions });
          }}
        />
      </motion.div>

      {/* Animated Footer */}
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 30 }}
        transition={{ duration: 0.6, delay: 1 }}
      >
        <div className="bg-[#1e3a5f] text-white rounded-lg p-8 text-center">
          <p className="text-sm text-[#d4975c] mb-2 tracking-wide uppercase">Confidential - For Internal Use Only</p>
          <p className="text-slate-300">
            This evaluation framework assesses vendors on immediate utility and long-term organizational AI capacity building.
          </p>
          <div className="mt-4 pt-4 border-t border-[#2a4a6f]">
            <p className="text-xs text-slate-400">
              Framework designed for Fortune 500 executive decision-making • Version {reportData.version}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Invisible sentinel for scroll-to-bottom detection (Glean demo) */}
      {isGleanDemo && <div ref={bottomSentinelRef} className="h-1" aria-hidden="true" />}

      {/* LLM Selection Modal for Rerun (admin only) */}
      <LLMSelectionModal
        isOpen={showRerunModal}
        onClose={() => setShowRerunModal(false)}
        onConfirm={handleRerunEvaluation}
        title="Rerun Evaluation"
      />

      {/* Custom Prompts Modal (all users) */}
      <CustomPromptsModal
        isOpen={showCustomPromptsModal}
        onClose={() => setShowCustomPromptsModal(false)}
        onConfirm={handleCustomPromptsRerun}
        vendorId={evaluation?.vendor_id}
        previousEvaluations={vendorEvaluations}
      />

      {/* Custom Prompts Diff Modal (view changes on reports with custom prompts) */}
      {evaluation?.custom_prompts && (
        <CustomPromptsDiffModal
          isOpen={showCustomPromptsDiffModal}
          onClose={() => setShowCustomPromptsDiffModal(false)}
          customPrompts={evaluation.custom_prompts}
        />
      )}
    </div>
  );
}
